import { useMediaQuery } from "react-responsive";

import {
  Badge,
  Button,
  Card,
  Heading,
  Icon,
  Span,
} from "@icg360/design-system";

import { Stack } from "components/common/stack";
import {
  type ThumbtackModalOpenTracking,
  useThumbtackModalContext,
} from "components/thumbtack-modal";
import { screenSizes } from "consts";
import type {
  InspectionAssetSource,
  KeystoneInspectionAsset,
  KeystoneReferral,
} from "data/keystone-api-mapping";
import { usePolicyDetail } from "data/ss-data-hooks";
import {
  capitalize,
  checkForNaStringValue,
  formatDate,
  localTimeFromTimestamp,
  trackEvent,
} from "utils";

import { useInspectionsData } from "./hooks";
import type { GalleryOpenPayload, StatusAppearance, StatusText } from "./index";
import { InspectionThumbnailStrip } from "./inspection-thumbnail-strip";
import { sortInspectionAssetIdsByLastUpdatedDesc } from "./inspections-helpers";
import {
  formatReferralWriteOutCardSubheading,
  getReferralReasonings,
  getReferralWriteOutCardHeading,
} from "./referral-display";
import { type UploadDrawerData } from "./upload-drawer";
import styles from "./write-out-card.module.scss";

export const WriteOutCard = ({
  referral,
  showGallery,
  showUploadDrawer,
  roofMarketplaceEligible,
  roofMarketplaceDrawerAvailable,
  onOpenRoofMarketplace,
}: {
  referral: KeystoneReferral;
  showGallery: (payload: GalleryOpenPayload) => void;
  showUploadDrawer: (uploadData: UploadDrawerData) => void;
  /** Inspection + feature flag qualify for RoofMarketplace (API may still route to Thumbtack). */
  roofMarketplaceEligible?: boolean;
  /** Service-area check returned `canService`; used for test id / drawer vs contractor UX. */
  roofMarketplaceDrawerAvailable?: boolean;
  /** Resolves RoofMarketplace drawer vs Thumbtack (parent prefetches service availability). */
  onOpenRoofMarketplace?: () => void | Promise<void>;
}) => {
  const { data } = usePolicyDetail();
  const { latestInspection } = useInspectionsData();
  const { zip: zipCode } = data?.userDetails?.propertyAddress ?? {};
  const { agencyName } = data?.userInsuranceRep ?? {};
  const { openThumbtackModal } = useThumbtackModalContext();

  const thumbtackTracking = (): ThumbtackModalOpenTracking => ({
    referralId: referral.id ?? "",
    category: referral.category,
    searchTerm: referral.searchTerm,
  });
  const isMobile = useMediaQuery({ maxWidth: screenSizes.tablet });
  const maxPhotosInRow = isMobile ? 3 : 5;
  const inspectionId = latestInspection?.id ?? "";
  const inspectionAssetObjects =
    latestInspection?.review?.inspectionReviewAssets ?? [];
  const {
    actionStatement,
    category,
    inspectionReviewAssetIds,
    inspectionReviewProofAssetIds,
    protectionCategory,
    searchTerm,
  } = referral;

  const getLatestAssets = (
    ids: string[] | undefined,
  ): KeystoneInspectionAsset[] => {
    if (!ids?.length) return [];
    const sortedIds = sortInspectionAssetIdsByLastUpdatedDesc(
      ids,
      inspectionAssetObjects,
    );
    const byId = new Map(inspectionAssetObjects.map((a) => [a.id, a] as const));
    return sortedIds
      .map((id) => byId.get(id))
      .filter((a): a is KeystoneInspectionAsset => a != null);
  };

  const writeOutHeading = getReferralWriteOutCardHeading(referral);
  const reasonings = getReferralReasonings(referral);
  const actionStatementDisplay = checkForNaStringValue(actionStatement ?? "");
  const validSearchTerm = checkForNaStringValue(searchTerm ?? "");
  const subHeading = formatReferralWriteOutCardSubheading(referral);
  const isReferralSubmitted: boolean =
    inspectionReviewProofAssetIds?.length > 0;
  const isResolved = referral?.isResolved;
  let statusText: StatusText = "Not started";
  let statusAppearance: StatusAppearance = "warning";
  if (isReferralSubmitted && !isResolved) {
    statusText = "Submitted";
    statusAppearance = "neutral";
  } else if (isResolved) {
    statusText = "Resolved";
    statusAppearance = "success";
  }

  const latestProofAsset = getLatestAssets(inspectionReviewProofAssetIds)[0];
  const latestUploadDate = formatDate(
    latestProofAsset?.lastUpdatedTimestamp ?? "",
  );
  const latestUploadTime = localTimeFromTimestamp(
    latestProofAsset?.lastUpdatedTimestamp ?? "",
  );
  const assetSourceDisplay: Record<InspectionAssetSource, string> = {
    POLICYHOLDER: latestProofAsset?.lastUpdatedByAppUserId ?? "",
    PRODUCER: agencyName ? `a representative at ${agencyName}` : "",
    UNDERWRITER: "SageSure support",
    VENDOR: "",
  };
  const latestUploaderDisplay =
    assetSourceDisplay[
      latestProofAsset?.assetSource as InspectionAssetSource
    ] ?? "";

  const renderAssetList = ({
    assets,
    heading,
    openTabIndex,
  }: {
    assets: KeystoneInspectionAsset[];
    heading: string;
    openTabIndex: 0 | 1;
  }) => {
    if (!assets.length) return null;

    const galleryPayload: GalleryOpenPayload = {
      inspectionAssetIds: inspectionReviewAssetIds ?? [],
      proofAssetIds: inspectionReviewProofAssetIds ?? [],
      title: capitalize(writeOutHeading ?? ""),
      statusText,
      statusAppearance,
      initialTabIndex: openTabIndex,
    };
    const openGallery = (activeAssetId?: string) => {
      trackEvent("inspections gallery opened", {
        numberOfAssets: assets.length,
        assetNames: assets.map((a) => `${a.assetFileName}`),
        writeOutHeading: capitalize(writeOutHeading ?? ""),
        clickedAssetType: openTabIndex === 0 ? "inspection" : "proof",
      });
      showGallery({
        ...galleryPayload,
        activeAssetId,
      });
    };

    return (
      <InspectionThumbnailStrip
        heading={heading}
        maxPhotosInRow={maxPhotosInRow}
        onPreviewClick={openGallery}
        orderedItems={assets.map((a) => ({ id: a.id, asset: a }))}
      />
    );
  };

  return (
    <Card>
      <div className={styles.categoryHeader}>
        <Icon name="HomeProtection" />
        <Heading size="eyebrow" className={styles.categoryHeading}>
          {protectionCategory}
        </Heading>
      </div>
      <Stack gap="xl">
        <div className={styles.heading}>
          <div className={styles.titleAndSubheading}>
            {writeOutHeading ? (
              <Heading size="xs">{capitalize(writeOutHeading)}</Heading>
            ) : null}
            <p className={styles.sub}>{subHeading}</p>
          </div>

          <div className={styles.statusAndButton}>
            <Badge.Indicator size="medium" appearance={statusAppearance}>
              {statusText}
            </Badge.Indicator>
            {!isResolved ? (
              <Button
                className={styles.uploadFilesButton}
                size="sm"
                fluid={isMobile}
                onPress={() =>
                  showUploadDrawer({
                    actionStatement: actionStatement ?? "",
                    issue: writeOutHeading ?? "",
                    referralId: referral?.id ?? "",
                    inspectionId,
                    protectionCategory: referral?.protectionCategory ?? "",
                    status: { statusText, statusAppearance },
                    subHeading,
                  })
                }
                data-testid="write-out-verify-safeguard-button"
              >
                Upload files & photos
              </Button>
            ) : null}
          </div>
        </div>

        {actionStatementDisplay && !isResolved ? (
          <div className={styles.resolveBox}>
            <Heading size="xxs">How to resolve</Heading>
            <p>{actionStatementDisplay}</p>

            {(category ?? "").toLowerCase() === "roof" &&
            onOpenRoofMarketplace &&
            (roofMarketplaceEligible || validSearchTerm) ? (
              <Span>
                Need help?{" "}
                <Button
                  appearance="link"
                  onPress={() => {
                    if (roofMarketplaceEligible) {
                      void onOpenRoofMarketplace?.();
                      return;
                    }
                    if (validSearchTerm) {
                      trackEvent("write-out-contractor button pressed", {
                        contractorSearchTerm: validSearchTerm,
                        writeOutHeading: capitalize(writeOutHeading ?? ""),
                        subHeading,
                      });
                      trackEvent(
                        "Inspection write-out: Contractor help clicked",
                        {
                          source: "thumbtack",
                          referralId: referral.id ?? "",
                          category: referral.category,
                        },
                      );
                      openThumbtackModal(
                        zipCode ?? "",
                        validSearchTerm,
                        undefined,
                        thumbtackTracking(),
                      );
                    }
                  }}
                  data-testid={
                    roofMarketplaceEligible && roofMarketplaceDrawerAvailable
                      ? "write-out-roof-marketplace-or-contractor-button"
                      : "write-out-contractor-button"
                  }
                  {...(validSearchTerm
                    ? { "data-contractor-search-term": validSearchTerm }
                    : {})}
                >
                  Find a qualified contractor.
                </Button>
              </Span>
            ) : validSearchTerm ? (
              <Span>
                Need help?{" "}
                <Button
                  appearance="link"
                  onPress={() => {
                    trackEvent("write-out-contractor button pressed", {
                      contractorSearchTerm: validSearchTerm,
                      writeOutHeading: capitalize(writeOutHeading ?? ""),
                      subHeading,
                    });
                    trackEvent(
                      "Inspection write-out: Contractor help clicked",
                      {
                        source: "thumbtack",
                        referralId: referral.id ?? "",
                        category: referral.category,
                      },
                    );
                    openThumbtackModal(
                      zipCode ?? "",
                      validSearchTerm,
                      undefined,
                      thumbtackTracking(),
                    );
                  }}
                  data-testid="write-out-contractor-button"
                  data-contractor-search-term={validSearchTerm}
                >
                  Find a qualified contractor.
                </Button>
              </Span>
            ) : null}
          </div>
        ) : null}

        {reasonings.length ? (
          <div className={styles.reasoning}>
            <Heading size="xxs">Why does this need to be safeguarded?</Heading>
            {reasonings.map((reasoningString, idx) => (
              <p key={idx}>{reasoningString}</p>
            ))}
          </div>
        ) : null}

        {renderAssetList({
          assets: getLatestAssets(inspectionReviewAssetIds),
          heading: "Inspection photos",
          openTabIndex: 0,
        })}

        <div>
          {renderAssetList({
            assets: getLatestAssets(inspectionReviewProofAssetIds),
            heading: "Safeguard verification",
            openTabIndex: 1,
          })}
          <div className={styles.lastUpdatedMetadata}>
            {latestProofAsset && latestUploadDate ? (
              <p>
                Last updated{" "}
                {latestUploaderDisplay ? `by ${latestUploaderDisplay} ` : null}
                on {latestUploadDate}
                {latestUploadTime
                  ? ` at ${latestUploadTime.toLowerCase()}`
                  : null}
              </p>
            ) : null}
          </div>
        </div>
      </Stack>
    </Card>
  );
};
